import {
    AbsenceInput,
    BonusInput,
    DynamicFee,
    DynamicFeeInput,
    EffectiveFee,
    EffectiveFeeInput,
    Mission,
    MissionVacationSatus,
    PayrollBreakdown,
    ProfileType,
} from "../../../__generated__/graphql";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList } from "../../../navigations/RootNavigation";
import { useCallback } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";

export type FormData = {
    usedVacationMinutes: string; // in minutes
    totalWorkedDays: string; // in decimal days
    absences: Array<AbsenceInput>;
    bonuses?: BonusInput[];
    effectiveFees: EffectiveFee[];
    dynamicFees: DynamicFee[];

    totalWork100?: string;
    totalWork125?: string;
    totalWork150?: string;

    deltaWork: string; // in minutes
    deltaWorkMode?: "plus" | "minus";

    overtimePaid100?: string;
    overtimePaid125?: string;
};

export type PayrollBreakdownData = {
    deltaWork?: number; // in minutes
    usedVacationMinutes?: number; // in minutes
    usedVacationAmount?: number; // in cents
    totalWorkedDays?: number; // in decimal days
    totalWork100?: number; // in minutes
    totalWork125?: number; // in minutes
    totalWork150?: number; // in minutes
    absences?: Array<AbsenceInput>;
    bonuses?: Array<BonusInput>;
    effectiveFees?: Array<EffectiveFeeInput>;
    dynamicFees?: Array<DynamicFeeInput>;
    overtimePaid100?: number;
    overtimePaid125?: number;
};

export type PayrollBreakdownEditorProps = {
    payrollBreakdown: PayrollBreakdown;
    mission?: Mission;
    vacationStatus: MissionVacationSatus;
    navigation?: StackNavigationProp<RootStackParamList, "PayrollBreakdownEditor">;
    profileType: ProfileType;
    needWorkDays: boolean;
    isFromTimesheet: boolean;
    onValidData: (data: PayrollBreakdownData | null) => void;
    onChange(data: PayrollBreakdownData): void;
    timeSheetsNeedCompletion?: boolean;
    isEmptyPeriod?: boolean;
};

export const useValidateNumber = () => {
    const i18n = useI18n();
    return useCallback(
        (value: string | null | undefined) => {
            if (value === undefined || value === "") {
                return i18n.t("payroll_breakdown.error_not_set");
            }
            if (value && isNaN(Number(value))) {
                return i18n.t("payroll_breakdown.error_not_a_number");
            }
            return undefined;
        },
        [i18n],
    );
};

export const calculatePayrollBreakdownMaxWorkDays = ({
    contractStart,
    contractEnd,
    payrollBreakdownStart,
    payrollBreakdownEnd,
}: {
    contractStart?: string | null;
    contractEnd?: string | null;
    payrollBreakdownStart: string;
    payrollBreakdownEnd: string;
}) => {
    let max = 31; // 1 month by default
    if (contractEnd && contractStart) {
        max = Math.ceil(
            (new Date(contractEnd).getTime() - new Date(contractStart).getTime()) /
                (1000 * 60 * 60 * 24),
        );
    }
    const payrollBreakdownDays = Math.ceil(
        new Date(payrollBreakdownEnd).getTime() -
            new Date(payrollBreakdownStart).getTime() / (1000 * 60 * 60 * 24),
    );
    return Math.min(payrollBreakdownDays, max);
};
